import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { formatDateTime } from "@/lib/utils";
import { useLazyTableParams } from "@/lib/useLazyTableParams";
import { useListPageParam } from "@/lib/useListPageParam";
import { useListSearchParam } from "@/lib/useListSearchParam";
import {
  useAdminCheckoutsList,
  type AdminCheckoutListItem,
} from "@/lib/admin-api";

function CheckoutStatusBadge({ status }: { status: string | undefined }) {
  const getSeverity = (status: string | undefined) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "APPROVED":
        return "success";
      case "CANCELLED":
        return "info";
      case "PARTIAL":
        return "info";
      case "MIXED":
        return "warning";
      case "COMPLETE":
        return "success";
      default:
        return null;
    }
  };

  if (!status) return null;

  return <Tag value={status} severity={getSeverity(status)} />;
}

const CHECKOUTS_PAGE_SIZE = 10;

export function CheckoutsList() {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useListSearchParam("q");
  const [pageFromUrl, setPageFromUrl] = useListPageParam();

  const {
    lazyParams,
    setLazyParams,
    onPage: onPageBase,
    onSort,
    resetToFirstPage: resetToFirstPageBase,
  } = useLazyTableParams({
    initialRows: CHECKOUTS_PAGE_SIZE,
    initialSortField: "createdAt",
    initialSortOrder: -1,
    initialFirst: (Math.max(1, pageFromUrl) - 1) * CHECKOUTS_PAGE_SIZE,
  });

  const onPage = useCallback(
    (event: { first: number; rows: number; page?: number }) => {
      onPageBase(event);
      const newPage = (event.page ?? Math.floor(event.first / event.rows)) + 1;
      setPageFromUrl(newPage);
    },
    [onPageBase, setPageFromUrl],
  );

  const resetToFirstPage = useCallback(() => {
    resetToFirstPageBase();
    setPageFromUrl(1);
  }, [resetToFirstPageBase, setPageFromUrl]);

  useEffect(() => {
    setLazyParams((prev) => {
      const expectedFirst = (Math.max(1, pageFromUrl) - 1) * prev.rows;
      if (prev.first === expectedFirst) return prev;
      return {
        ...prev,
        first: expectedFirst,
        page: Math.max(0, pageFromUrl - 1),
      };
    });
  }, [pageFromUrl, setLazyParams]);

  const filter = useMemo(() => {
    const search = globalFilter.trim();
    if (search === "") return undefined;
    return `bookingReference=like=${search}`;
  }, [globalFilter]);

  const checkoutsQuery = useAdminCheckoutsList({
    page: lazyParams.page + 1,
    pageSize: lazyParams.rows,
    sortField: lazyParams.sortField,
    sortOrder: lazyParams.sortOrder,
    filter,
  });
  const checkouts = checkoutsQuery.data?.data ?? [];
  const totalRecords = checkoutsQuery.data?.pagination.total ?? 0;
  const loading = checkoutsQuery.isFetching;

  const onFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    resetToFirstPage();
  };

  const bookingReferenceBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return rowData.bookingReference || "-";
  };

  const statusBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return <CheckoutStatusBadge status={rowData.calculatedStatus} />;
  };

  const totalAmountBodyTemplate = (rowData: AdminCheckoutListItem) => {
    const amount = rowData.totalCents ?? 0;
    if (amount === 0) return "Free";
    const currency = rowData.currency ?? "gbp";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const participantsCountBodyTemplate = (rowData: AdminCheckoutListItem) => {
    const participants = rowData.participants ?? [];
    const pending = participants.filter((p) => p.status === "PENDING").length;
    const approved = participants.filter((p) => p.status === "APPROVED").length;
    return (
      <div className="flex flex-column gap-1">
        <span>
          {pending > 0 && (
            <span className="text-orange-600 font-semibold">{pending} pending</span>
          )}
        </span>
        <span>
          {approved > 0 && (
            <span className="text-green-600">{approved} approved</span>
          )}
        </span>
        <span className="text-gray-600">
          {participants.length} total
        </span>
      </div>
    );
  };

  const dateBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return rowData.createdAt ? formatDateTime(rowData.createdAt) : "-";
  };

  const actionBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={() => {
            void navigate(`/checkouts/${rowData.id}`);
          }}
          aria-label="View"
        />
      </div>
    );
  };

  return (
    <div className="card">
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold">All Bookings</h1>
        <span className="p-input-icon-left w-full md:w-auto">
          <i className="pi pi-search" />
          <InputText
            value={globalFilter}
            onChange={onFilter}
            placeholder="Search by booking reference..."
            className="w-full"
          />
        </span>
      </div>

      <DataTable
        value={checkouts}
        dataKey="id"
        lazy
        paginator
        rows={lazyParams.rows}
        first={lazyParams.first}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        loading={loading}
        responsiveLayout="scroll"
        emptyMessage="No bookings found"
        className="text-sm"
      >
        <Column
          field="bookingReference"
          header="Booking Reference"
          body={bookingReferenceBodyTemplate}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="calculatedStatus"
          header="Status"
          body={statusBodyTemplate}
          style={{ minWidth: "120px" }}
        />
        <Column
          field="totalCents"
          header="Total Amount"
          body={totalAmountBodyTemplate}
          sortable
          style={{ minWidth: "120px" }}
        />
        <Column
          header="Participants"
          body={participantsCountBodyTemplate}
          style={{ minWidth: "150px" }}
        />
        <Column
          field="createdAt"
          header="Created"
          body={dateBodyTemplate}
          sortable
          style={{ minWidth: "160px" }}
        />
        <Column
          body={actionBodyTemplate}
          header="Actions"
          style={{ minWidth: "100px" }}
        />
      </DataTable>
    </div>
  );
}

